import { useI18n } from '@vista-digital/occ-ui'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'
import { RouterProvider, createBrowserRouter, createMemoryRouter, useLocation, useNavigate } from 'react-router-dom'
import { navigationService } from '../content/services/navigation.service'
import { urlService } from '../content/services/url.service'
import { getRoutes } from './getRoutes'
import { Layout } from './Layout'
import { PagesEnum } from '../content/pages/pages'

export interface PagesRouterOverrideProps extends LayoutWrapperOverrideProps {
    overridePageComponent?: {
        pageKey: PagesEnum
        component: FunctionComponent
    }
    overridePath?: string
    overrideSearchParams?: { [key: string]: string }
}

export const PagesRouter: FunctionComponent<PagesRouterOverrideProps> = ({
    overridePageComponent,
    overridePath,
    overrideSearchParams,
    ...layoutOverrideProps
}) => {
    urlService.setI18n(useI18n())

    const allRoutes = useMemo(() => getRoutes(overridePageComponent), [])

    const LayoutWrapperMemo = useMemo(() => () => <LayoutWrapper {...layoutOverrideProps} />, [layoutOverrideProps])

    const router = useMemo(() => {
        if (!allRoutes || allRoutes.length === 0) return null
        const routerArgs = [{ Component: LayoutWrapperMemo, children: allRoutes }]

        return overridePath
            ? createMemoryRouter(routerArgs, {
                  initialEntries: [
                      {
                          pathname: overridePath,
                          search: new URLSearchParams(overrideSearchParams ?? '').toString(),
                      },
                  ],
              })
            : createBrowserRouter(routerArgs)
    }, [overridePath, overrideSearchParams, allRoutes, LayoutWrapperMemo])

    if (allRoutes.length === 0 || !router) return null

    navigationService.setRoutes(allRoutes)

    return <RouterProvider router={router} />
}

interface LayoutWrapperOverrideProps {
    layoutOverride?: FunctionComponent
}

export const LayoutWrapper: FunctionComponent<LayoutWrapperOverrideProps> = props => {
    const navigate = useNavigate()
    const location = useLocation()

    const Element = useMemo(() => props?.layoutOverride ?? (() => <Layout />), [props?.layoutOverride])

    // Trailing slash not always added by React Router, but we always want to have one, especially for Cinemas, Popups and HelloBar
    if (!location.pathname.endsWith('/')) {
        location.pathname += '/'
        navigate(location, { replace: true })
        return null
    }

    navigationService.setNavigate(navigate)
    navigationService.setLocation(location)

    return <Element />
}
